import {
  CTA_SOURCES,
  PAGE_SECTIONS,
  ShowsNearbyEvents,
  UserEvents,
  getMixpanelDistinctId,
  trackEvent,
} from "@punchup/analytics";
import {
  convertToAffiliate,
  isTicketmasterUrl,
} from "@/utils/ticketmasterConverter";

import { Comedian } from "@/data/comedians";
import { ShowInfo } from "@/data/shows";
import { genWebAppURL } from "@punchup/utils/genAbsoluteURL";
import { useCallback } from "react";
import { useTixologiWidget } from "@/hooks/tixologi/widget";
import { useToast } from "@/shadcn/ui/use-toast";

interface UseShowRowAnalyticsParams {
  show: ShowInfo;
  comedian: Comedian;
  location: string;
  isNearby: boolean;
  internalReferrer?: string;
  shortCode?: string;
  isTicketmasterEvent: boolean;
  isTixologi: boolean;
  ticketmasterAffiliateEnabled: boolean;
  buyTicketsShowId?: string;
  setBuyTicketsShowId?: (id: string) => void;
  pageSection?: PAGE_SECTIONS;
  ctaSource?: CTA_SOURCES;
}

export function useShowRowAnalytics({
  show,
  comedian,
  location,
  isNearby,
  internalReferrer,
  shortCode,
  isTicketmasterEvent,
  isTixologi,
  ticketmasterAffiliateEnabled,
  buyTicketsShowId,
  setBuyTicketsShowId,
  pageSection,
  ctaSource,
}: UseShowRowAnalyticsParams) {
  const { open: openTixologiWidget } = useTixologiWidget();
  const { toast } = useToast();

  // Generate the shareable link
  const shareableLink = genWebAppURL(`/e/${show.id}`);

  const handleComedianNameClick = useCallback(
    (clickedComedian: { id: string; display_name: string }) => {
      trackEvent(ShowsNearbyEvents.CLICKED_COMEDIAN_NAME_TICKET_CARD, {
        ComedianId: clickedComedian.id,
        ComedianName: clickedComedian.display_name,
        ShowId: show.id,
        ShowVenue: show.venue,
        ShowDate: show.datetime.toISOString(),
        Source: location,
        IsNearby: isNearby,
        IsTicketmaster: isTicketmasterEvent,
        ...(internalReferrer && { InternalReferrer: internalReferrer }),
        ...(shortCode ? { shortLinkCode: shortCode } : {}),
      });
    },
    [show, location, isNearby, internalReferrer, shortCode, isTicketmasterEvent]
  );

  // Track-only: fire the BUY_TICKETS_CLICKED event without performing any
  // navigation. Used by the real-anchor buttons whose `target="_blank"` already
  // navigates — calling `handleBuyTicketsClick` there would `window.open` a
  // SECOND window (a popup the browser blocks). See ShowRowBuyButton.
  const trackBuyTickets = useCallback(
    ({ isVIPShow = false }: { isVIPShow?: boolean } = {}) => {
      trackEvent(
        UserEvents.BUY_TICKETS_CLICKED,
        {
          ComedianId: isTicketmasterEvent ? null : comedian.id,
          ComedianName: comedian.display_name,
          ShowId: isTicketmasterEvent ? null : show.id,
          ...(isTicketmasterEvent && { TicketmasterId: show.id }),
          ShowVenue: show.venue,
          ShowDate: show.datetime.toISOString(),
          Source: location,
          ...(internalReferrer && { InternalReferrer: internalReferrer }),
          ...(shortCode ? { shortLinkCode: shortCode } : {}),
          IsNearby: isNearby,
          IsVIPShow: isVIPShow,
          IsTixologi: isTixologi,
          ...(pageSection && ctaSource
            ? {
                cta_source: ctaSource,
                page_section: pageSection,
              }
            : {}),
        },
        { send_immediately: true }
      );
    },
    [
      show.id,
      show.venue,
      show.datetime,
      comedian.display_name,
      comedian.id,
      internalReferrer,
      isNearby,
      location,
      isTixologi,
      shortCode,
      isTicketmasterEvent,
      pageSection,
      ctaSource,
    ]
  );

  const handleBuyTicketsClick = useCallback(
    ({ isVIPShow = false }: { isVIPShow?: boolean }) => {
      trackBuyTickets({ isVIPShow });

      if (isTixologi) {
        // Toggle the iframe directly with local state for better performance
        openTixologiWidget(show);

        // For backward compatibility, also set the parent state, but with a slight delay
        // to prioritize the local state change and improve perceived performance
        if (setBuyTicketsShowId) {
          setTimeout(() => {
            setBuyTicketsShowId(buyTicketsShowId === show.id ? "" : show.id);
          }, 10);
        }
      } else {
        // Convert Ticketmaster URLs to affiliate links before opening
        const ticketLink =
          ticketmasterAffiliateEnabled && isTicketmasterUrl(show.ticketLink)
            ? convertToAffiliate(show.ticketLink, {
                src: location,
                distinctId: getMixpanelDistinctId(),
              })
            : show.ticketLink;
        window.open(ticketLink, "_blank");
      }
    },
    [
      trackBuyTickets,
      show,
      location,
      isTixologi,
      buyTicketsShowId,
      setBuyTicketsShowId,
      openTixologiWidget,
      ticketmasterAffiliateEnabled,
    ]
  );

  const handleShare = useCallback(() => {
    trackEvent(UserEvents.CLICKED_SHARE_SHOW, {
      ComedianId: comedian.id,
      ComedianName: comedian.display_name,
      ShowId: show.id,
      Source: location,
      IsNearby: isNearby,
      ...(internalReferrer && { InternalReferrer: internalReferrer }),
    });

    if (navigator.share) {
      navigator
        .share({
          title: `${comedian.display_name} at ${show.venue}`,
          text: `Check out ${comedian.display_name} at ${show.venue} on ${show.datetime.format("MMM D")}!`,
          url: shareableLink,
        })
        .catch((error) => console.log("Error sharing", error));
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(shareableLink);
      toast({
        title: "Link copied to clipboard",
        description: "You can now paste and share it anywhere!",
        duration: 3000,
      });
    }
  }, [
    comedian.display_name,
    comedian.id,
    internalReferrer,
    isNearby,
    location,
    shareableLink,
    show.datetime,
    show.id,
    show.venue,
    toast,
  ]);

  return {
    handleComedianNameClick,
    trackBuyTickets,
    handleBuyTicketsClick,
    handleShare,
    shareableLink,
  };
}
